// Embeds a self-hosted draw.io (diagrams.net, Apache-2.0) editor for Lucidchart-style visual
// diagramming: users draw from the shape/stencil + icon libraries (AWS/Azure/UML/network/…),
// and we own storage (the diagram XML) + export-to-code. draw.io does the palette + drawing;
// this component is just the iframe + postMessage bridge.
//
// Protocol: https://www.drawio.com/doc/faq/embed-mode  (embed=1&proto=json).
//   iframe → parent:  {event:'init'|'save'|'autosave'|'export'|'exit'|'configure'}
//   parent → iframe:  {action:'load'|'merge'|'export'|'layout'|'fit', ...}
// Diagram data is exchanged over postMessage only — it never appears in the URL and is never
// sent to diagrams.net (the app itself is served from our own origin).

import React, { useEffect, useRef } from 'react';

// Path to the self-hosted draw.io webapp (vendored under our static assets — see infra step).
// Overridable via a build-time env for a separate static host (e.g. drawio.getfxm.ai).
const DRAWIO_BASE = (import.meta as any).env?.VITE_DRAWIO_URL || '/drawio/index.html';

type DrawioEvent =
  | { event: 'init' }
  | { event: 'load'; xml?: string }
  | { event: 'save'; xml?: string; modified?: boolean }
  | { event: 'autosave'; xml?: string }
  | { event: 'export'; data?: string; format?: string }
  | { event: 'exit'; modified?: boolean }
  | { event: 'configure' };

export type DrawioHandle = {
  /** Ask draw.io to export the current diagram. Result arrives via onExport. */
  requestExport: (format?: 'svg' | 'xmlsvg' | 'png' | 'xmlpng') => void;
};

export type DrawioEditorProps = {
  xml: string; // current mxGraphModel XML; '' → blank diagram
  onSave: (xml: string) => void; // called on save + autosave
  dark?: boolean;
  onExport?: (data: string, format: string) => void;
  onReady?: (handle: DrawioHandle) => void;
  className?: string;
};

export function DrawioEditor({
  xml,
  onSave,
  dark,
  onExport,
  onReady,
  className,
}: DrawioEditorProps): React.ReactElement {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const xmlRef = useRef(xml);
  xmlRef.current = xml;

  useEffect(() => {
    const post = (msg: unknown): void =>
      frameRef.current?.contentWindow?.postMessage(JSON.stringify(msg), '*');

    const onMessage = (e: MessageEvent): void => {
      // Only accept messages from our own iframe.
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      let msg: DrawioEvent;
      try {
        msg = typeof e.data === 'string' ? JSON.parse(e.data) : (e.data as DrawioEvent);
      } catch {
        return; // not our protocol
      }
      switch (msg.event) {
        case 'init':
          // Editor is ready → hand it the diagram (over postMessage, not the URL) + enable autosave.
          post({ action: 'load', xml: xmlRef.current || '', autosave: 1 });
          onReady?.({
            requestExport: (format = 'xmlsvg') =>
              post({ action: 'export', format, spinKey: 'export' }),
          });
          break;
        case 'save':
        case 'autosave':
          if (msg.xml != null) onSave(msg.xml);
          break;
        case 'export':
          if (msg.data != null) onExport?.(msg.data, msg.format ?? 'svg');
          break;
        default:
          break; // exit/configure/load: nothing to do (host owns lifecycle)
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onSave, onExport, onReady]);

  // Hide the open-source draw.io GitHub badge (bottom-right corner). draw.io is served
  // same-origin (/drawio/), so we can inject a CSS rule into the iframe document; it applies
  // even though draw.io adds the badge lazily after load.
  const stripBranding = (): void => {
    try {
      const idoc = frameRef.current?.contentDocument;
      if (!idoc || idoc.getElementById('fx-drawio-nobrand')) return;
      const style = idoc.createElement('style');
      style.id = 'fx-drawio-nobrand';
      style.textContent = 'a[href*="github.com/jgraph/drawio"]{display:none!important;}';
      idoc.head?.appendChild(style);
    } catch {
      /* not same-origin / not ready — ignore */
    }
  };

  const params = new URLSearchParams({
    embed: '1',
    proto: 'json',
    libraries: '1', // shape/stencil + icon libraries (the Lucidchart-style palette)
    spin: '1', // loading spinner until we send the diagram
    noExitBtn: '1',
    saveAndExit: '0',
    ui: dark ? 'dark' : 'kennedy',
  });

  return (
    <iframe
      ref={frameRef}
      className={className}
      title="Diagram editor"
      src={`${DRAWIO_BASE}?${params.toString()}`}
      onLoad={stripBranding}
      style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
    />
  );
}
